import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TableHeadComponent } from "../../../common/TableHead";
import { integrationsMatcher } from "../../../utils/integrationsMatcher";
import { Dispatcher, SiteInfo } from "./SiteAboutPageUtils/siteAboutInterfaces";
import {
  Alert,
  IconButton,
  InputAdornment,
  Switch,
  TableHead,
  Typography,
} from "@mui/material";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { SeeNotesDialog } from "./SeeNotesDialog";
import { epochToUPCCovertor } from "./SiteAboutPageUtils/epochToUPCCovertor";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { timeSinceConvertor } from "./SiteAboutPageUtils/timeSinceConvertor";
import { roundToHundredth } from "./SiteAboutPageUtils/roundToHundredth";
import { AboutTableHead } from "./AboutTableHead";
import { checkedCircleIcon } from "../../../common/checkedCircleIcon";
import { cancelIconRed } from "../../../common/cancelIconRed";
import { useAppStore } from "../../../../appStore";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createDots } from "../../../common/createDots";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { getVmsDisplayLabel } from "./SiteAboutPageUtils/getVmsDisplayLabel";
import { visibleVmsVariables } from "./SiteAboutPageUtils/visibleVmsVariables";
import { readsSettingsFile } from "./SiteAboutPageUtils/readsSettingsFile";
import { siteMotionStreamingAdvice } from "./SiteAboutPageUtils/motionStreamingAdvice";

const COLUMN_COUNT = 7;
const CELL_WIDTH = `${100 / COLUMN_COUNT}%`;

const padTitlesToColumnCount = (titles: string[]) => {
  const padded = [...titles];
  while (padded.length < COLUMN_COUNT) {
    padded.push("");
  }
  return padded.slice(0, COLUMN_COUNT);
};

const renderBlankCells = (count: number, keyPrefix: string) =>
  Array.from({ length: Math.max(0, count) }).map((_, index) => (
    <TableCell
      key={`${keyPrefix}-${index}`}
      align="left"
      sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
    />
  ));

export const GeneralInfoTab = ({
  siteInfo,
  setSiteInfo,
  setActiveTab,
  vmsVariables,
}: {
  siteInfo: SiteInfo;
  setSiteInfo: Dispatcher<SiteInfo>;
  setActiveTab: Dispatcher<JSX.Element>;
  vmsVariables: any;
}) => {
  const { userPermissions, wireGuardOptions } = useAppStore();
  const { isFeatureEnabled } = useFeatureFlag();
  const showWireguard = isFeatureEnabled("showWireguard", false);
  const shouldShowWireguardAvailabilityZone =
    showWireguard && Boolean(siteInfo.display_wireguard);

  const autopatrolSite = siteInfo.integration_type_name === "autopatrol";

  // AI Link / Sentinel Verifier / Clips sites don't read from the settings
  // file, so "Last Synchronized" is meaningless for them — hide it.
  const showLastSynchronized = readsSettingsFile(
    siteInfo.integration_type_name
  );

  const alarmOptions = JSON.parse(localStorage.alarmOptions);

  const alarmIds = new Map();
  alarmOptions.forEach((object: any) => {
    alarmIds.set(object.value, object.name);
  });

  // RTSP + motion: recommend HTTP streaming (ENG-2828). Server-decided.
  const motionStreamingAdvice = siteMotionStreamingAdvice(siteInfo);

  const noVMS =
    siteInfo.integration_type_name === "rtsp" &&
    siteInfo.onboarding_integration === 1;

  const titlesArray = !autopatrolSite
    ? [
        "Site Name",
        "Timezone",
        "Autoadd",
        "Integration Type",
        "Alarm Type",
        "Parent Group",
      ]
    : ["Site Name", "Timezone", "Integration Type", "Parent Group"];

  if (siteInfo.show_ecs_nat_instance) {
    titlesArray.push("Whitelisting");
  }

  const paddedTitlesArray = padTitlesToColumnCount(titlesArray);

  const statsTitles = !autopatrolSite
    ? [
        ...(showLastSynchronized ? ["Last Synchronized"] : []),
        "Last Restart",
        "Last Motion",
        "Last Alert",
        "Next Schedule",
        "Motion %",
        "Notes",
      ]
    : [
        ...(showLastSynchronized ? ["Last Synchronized"] : []),
        "Last Restart",
        "Last Alert",
        "Next Schedule",
        "Notes",
      ];
  const paddedStatsTitles = padTitlesToColumnCount(statsTitles);

  const [seeNotesDialogOpen, setSeeNotesDialogOpen] = React.useState(false);

  const [showPassword, setShowPassword] = React.useState<any>({});

  const handleClickShowPassword = (inputString: string) =>
    setShowPassword((previousState: any) => ({
      ...previousState,
      [inputString]: !previousState[inputString],
    }));

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  React.useEffect(() => {
    vmsVariables.forEach((array: string[]) => {
      if (array[3] === "password") {
        setShowPassword((show: any) => ({ ...show, [array[2]]: false }));
      }
    });
  }, [vmsVariables]);

  const renderVMSCell = (array: string[]) => {
    if (array[3] === "checkbox") {
      return (
        <TableCell
          key={array[2]}
          align="left"
          sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
        >
          <Switch disabled checked={array[1] === "true"} color="warning" />
        </TableCell>
      );
    }

    if (array[3] === "password") {
      return (
        <TableCell
          key={array[2]}
          align="left"
          sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
        >
          {userPermissions.view_passwords && (
            <div className="flex items-center">
              {showPassword[array[2]]
                ? siteInfo[array[2] as keyof typeof siteInfo]
                : createDots(
                    siteInfo[array[2] as keyof typeof siteInfo]?.toString() ||
                      ""
                  )}
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword[array[2]]
                      ? "hide the password"
                      : "display the password"
                  }
                  onClick={() => {
                    handleClickShowPassword(array[2]);
                  }}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                  size="small"
                >
                  {showPassword[array[2]] ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            </div>
          )}
          {!userPermissions.view_passwords && (
            <div className="flex items-center">
              {createDots(
                siteInfo[array[2] as keyof typeof siteInfo]?.toString() || ""
              )}
            </div>
          )}
        </TableCell>
      );
    }

    return (
      <TableCell
        key={array[2]}
        align="left"
        sx={{
          fontFamily: "Mulish",
          width: CELL_WIDTH,
          // Multi-line values (textarea fields) keep one entry per line.
          ...(array[3] === "textarea" ? { whiteSpace: "pre-line" } : {}),
        }}
      >
        {siteInfo[array[2] as keyof typeof siteInfo]
          ? siteInfo[array[2] as keyof typeof siteInfo]
          : "--"}
      </TableCell>
    );
  };

  const renderVMSTable = () => {
    if (noVMS) {
      return (
        <TableBody>
          <TableRow>
            <TableCell
              colSpan={COLUMN_COUNT}
              align="left"
              sx={{ fontFamily: "Mulish" }}
            >
              <Typography variant="body1" sx={{ fontFamily: "Mulish" }}>
                {
                  "To view connection details for specific cameras, find the camera below, then > Show More > Edit Camera > Camera Information > Connection."
                }
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    // ENG-2535: fields whose visible_when rule fails are not shown.
    const shownVariables = visibleVmsVariables(vmsVariables, siteInfo).map(
      ({ array }) => array
    );
    const totalRows = Math.ceil(shownVariables.length / COLUMN_COUNT);
    const rows = [];

    for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
      const startIndex = rowIndex * COLUMN_COUNT;
      const endIndex = Math.min(
        startIndex + COLUMN_COUNT,
        shownVariables.length
      );
      const rowVariables = shownVariables.slice(startIndex, endIndex);
      const emptyCells = COLUMN_COUNT - rowVariables.length;

      rows.push(
        <React.Fragment key={`vms-row-${rowIndex}`}>
          <TableHead>
            <TableRow>
              {rowVariables.map((array: string[]) => (
                <TableCell
                  key={array[2] || array[0]}
                  align="left"
                  sx={{
                    width: CELL_WIDTH,
                    fontFamily: "Mulish",
                    fontWeight: "Bold",
                  }}
                >
                  {getVmsDisplayLabel(array[0], siteInfo.integration_type_name)}
                </TableCell>
              ))}
              {renderBlankCells(emptyCells, `vms-header-empty-${rowIndex}`)}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key={`${siteInfo.id}-vms-data-row-${rowIndex}`}>
              {rowVariables.map((array: string[]) => renderVMSCell(array))}
              {renderBlankCells(emptyCells, `vms-body-empty-${rowIndex}`)}
            </TableRow>
          </TableBody>
        </React.Fragment>
      );
    }

    return rows;
  };

  const siteInfoBodyCellCount =
    (!autopatrolSite ? 6 : 4) + (siteInfo.show_ecs_nat_instance ? 1 : 0);

  const statsBodyCellCount =
    (showLastSynchronized ? 1 : 0) +
    1 + // Last Restart
    (!autopatrolSite ? 1 : 0) + // Last Motion
    1 + // Last Alert
    1 + // Next Schedule
    (!autopatrolSite ? 1 : 0) + // Motion %
    1; // Notes

  return (
    <div>
      {seeNotesDialogOpen && (
        <SeeNotesDialog
          seeNotesDialogOpen={seeNotesDialogOpen}
          setSeeNotesDialogOpen={setSeeNotesDialogOpen}
          defaultText={siteInfo.notes}
        />
      )}

      <TableContainer component={Paper} sx={{ width: "100%" }}>
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <TableHeadComponent titlesArray={paddedTitlesArray} />
          <TableBody>
            <TableRow key={siteInfo.id}>
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                {siteInfo.name}
              </TableCell>
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                {siteInfo.timezone}
              </TableCell>
              {!autopatrolSite && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
                >
                  {siteInfo.auto_add ? (
                    userPermissions.super_user ? (
                      <Tooltip
                        title={siteInfo.auto_add.toString()}
                        placement="top"
                      >
                        {checkedCircleIcon}
                      </Tooltip>
                    ) : (
                      checkedCircleIcon
                    )
                  ) : (
                    cancelIconRed
                  )}
                </TableCell>
              )}
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                {integrationsMatcher("Name", siteInfo.integration_type)}
              </TableCell>
              {!autopatrolSite && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
                >
                  {alarmIds.get(siteInfo.default_alarm)}
                </TableCell>
              )}
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                {siteInfo.parent_group.name}
              </TableCell>
              {siteInfo.show_ecs_nat_instance && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
                >
                  {siteInfo.ecs_nat_instance ? (
                    <Switch defaultChecked disabled={true} color="warning" />
                  ) : (
                    <Switch checked={false} disabled={true} color="warning" />
                  )}
                </TableCell>
              )}
              {renderBlankCells(
                COLUMN_COUNT - siteInfoBodyCellCount,
                "site-info-empty"
              )}
            </TableRow>
          </TableBody>
        </Table>
        {shouldShowWireguardAvailabilityZone && (
          <Table
            sx={{
              width: "100%",
              fontFamily: "Mulish",
              tableLayout: "fixed",
            }}
          >
            <TableHeadComponent
              titlesArray={padTitlesToColumnCount([
                "WireGuard Availability Zone",
              ])}
            />
            <TableBody>
              <TableRow>
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
                >
                  {wireGuardOptions.find(
                    (option) => option.id === siteInfo.wireguard
                  )?.name || "--"}
                </TableCell>
                {renderBlankCells(COLUMN_COUNT - 1, "wireguard-empty")}
              </TableRow>
            </TableBody>
          </Table>
        )}
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <AboutTableHead
            titlesArray={paddedStatsTitles}
            cellWidth={CELL_WIDTH}
          />
          <TableBody>
            <TableRow>
              {showLastSynchronized && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
                >
                  {siteInfo.deployed_date
                    ? epochToUPCCovertor(
                        siteInfo.deployed_date,
                        siteInfo.timezone
                      ).toString()
                    : "--"}
                  &nbsp;
                  {siteInfo.deployed_date && siteInfo.timezone ? (
                    <Tooltip
                      title={siteInfo.timezone}
                      placement="top"
                      sx={{ fontSize: "15px", fontFamily: "mulish" }}
                    >
                      <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </TableCell>
              )}
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                {siteInfo.pod_boot_time
                  ? epochToUPCCovertor(
                      siteInfo.pod_boot_time,
                      siteInfo.timezone
                    ).toString()
                  : "--"}
                &nbsp;
                {siteInfo.pod_boot_time && siteInfo.timezone ? (
                  <Tooltip
                    title={siteInfo.timezone}
                    placement="top"
                    sx={{ fontSize: "15px", fontFamily: "mulish" }}
                  >
                    <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                  </Tooltip>
                ) : (
                  ""
                )}
                {siteInfo.pod_boot_time &&
                siteInfo.deployed_date &&
                siteInfo.deployed_date > siteInfo.pod_boot_time ? (
                  <Tooltip
                    title="Settings synced after last restart — rearm site to restart and apply changes."
                    placement="top"
                    sx={{ fontSize: "15px", fontFamily: "mulish" }}
                  >
                    <WarningAmberIcon
                      fontSize="small"
                      sx={{ color: "#ed6c02", verticalAlign: "middle" }}
                    />
                  </Tooltip>
                ) : (
                  ""
                )}
              </TableCell>
              {!autopatrolSite && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
                >
                  {siteInfo.last_motion
                    ? timeSinceConvertor(siteInfo.last_motion)
                    : "--"}
                </TableCell>
              )}
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                {siteInfo.last_alert
                  ? timeSinceConvertor(siteInfo.last_alert)
                  : "--"}
              </TableCell>
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                {siteInfo.next_schedule ? (
                  <>
                    {epochToUPCCovertor(
                      siteInfo.next_schedule,
                      siteInfo.timezone
                    ).toString()}
                    &nbsp;
                    {siteInfo.next_schedule && siteInfo.timezone ? (
                      <Tooltip
                        title={siteInfo.timezone}
                        placement="top"
                        sx={{ fontSize: "15px", fontFamily: "mulish" }}
                      >
                        <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                      </Tooltip>
                    ) : (
                      ""
                    )}
                  </>
                ) : (
                  "--"
                )}
              </TableCell>
              {!autopatrolSite && (
                <TableCell
                  align="left"
                  sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
                >
                  {siteInfo.motion_percentage
                    ? `${roundToHundredth(siteInfo.motion_percentage)}%`
                    : "--"}
                </TableCell>
              )}
              <TableCell
                align="left"
                sx={{ fontFamily: "Mulish", width: CELL_WIDTH }}
              >
                <OrangeTextButton
                  text="See Notes"
                  onClick={() => {
                    setSeeNotesDialogOpen(true);
                  }}
                  disabled={false}
                />
              </TableCell>
              {renderBlankCells(
                COLUMN_COUNT - statsBodyCellCount,
                "stats-empty"
              )}
            </TableRow>
          </TableBody>
        </Table>
        <Table
          sx={{
            width: "100%",
            fontFamily: "Mulish",
            tableLayout: "fixed",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                colSpan={COLUMN_COUNT}
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "bold",
                  fontSize: "16px",
                }}
              >
                VMS
              </TableCell>
            </TableRow>
          </TableHead>
          {renderVMSTable()}
        </Table>
        {motionStreamingAdvice && (
          <Alert
            severity="info"
            data-testid="motion-streaming-advice"
            sx={{ fontFamily: "Mulish", m: 2 }}
          >
            {motionStreamingAdvice}
          </Alert>
        )}
      </TableContainer>
    </div>
  );
};
